import { useEffect, useMemo, useRef, useState } from 'react'
import { fmt } from '../lib/format'

export type ChartSeries = { key: string; label: string; values: (number | null)[]; dashed?: boolean }

export const PALETTE = [1, 2, 3, 4, 5, 6, 7, 8].map(i => `var(--c${i})`)

/** Round tick steps: 1, 2, 2.5, 5 × 10^n, about `count` of them across the range. */
function niceTicks(lo: number, hi: number, count = 4): number[] {
  if (lo === hi) {
    const d = Math.abs(lo) || 1
    lo -= d * 0.5
    hi += d * 0.5
  }
  const raw = (hi - lo) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].find(s => s * mag >= raw) ?? 10) * mag
  const out: number[] = []
  for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.001; v += step) out.push(Math.abs(v) < step * 1e-9 ? 0 : v)
  return out
}

/**
 * A chart of one measure over time: lines (trends, versions compared) or bars (stacked for
 * composition, when asked and every value is positive; side by side otherwise). Closed periods are shaded as actuals. Hover reads
 * every series at a period; the legend toggles series.
 */
export function Chart({
  x,
  series,
  format,
  mode = 'line',
  height = 220,
  compact = false,
  stack = false,
}: {
  x: { id: string; label: string; closed?: boolean; period?: { months: number; closedMonths: number } | null }[]
  series: ChartSeries[]
  format: string
  mode?: 'line' | 'bar'
  height?: number
  compact?: boolean
  /** the series are parts of a whole */
  stack?: boolean
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(560)
  const [hover, setHover] = useState<number | null>(null)
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.max(240, e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const shown = series.filter(s => !hidden.has(s.key))
  const stacked = stack && mode === 'bar' && shown.length > 1 && shown.every(s => s.values.every(v => v === null || v >= 0))
  const { lo, hi, ticks } = useMemo(() => {
    const vals: number[] = []
    if (stacked) for (let i = 0; i < x.length; i++) vals.push(shown.reduce((s, se) => s + (se.values[i] ?? 0), 0))
    else for (const s of shown) for (const v of s.values) if (v !== null) vals.push(v)
    if (mode === 'bar') vals.push(0)
    if (!vals.length) return { lo: 0, hi: 1, ticks: [0, 1] }
    let l = Math.min(...vals)
    let h = Math.max(...vals)
    if (mode === 'line') {
      const pad = (h - l) * 0.08 || Math.abs(h) * 0.05 || 1
      l = l >= 0 && l - pad < 0 ? 0 : l - pad
      h += pad
    }
    const t = niceTicks(l, h, compact ? 3 : 4)
    return { lo: Math.min(l, t[0] ?? l), hi: Math.max(h, t.at(-1) ?? h), ticks: t }
  }, [shown, stacked, mode, x, compact])

  const H = height
  const pl = compact ? 44 : 56
  const pr = 10
  const pt = 10
  const pb = 24
  const W = width
  const n = x.length
  const band = (W - pl - pr) / Math.max(1, n)
  const cx = (i: number) => (mode === 'bar' ? pl + band * (i + 0.5) : pl + (n <= 1 ? (W - pl - pr) / 2 : (i * (W - pl - pr)) / (n - 1)))
  const y = (v: number) => pt + (1 - (v - lo) / (hi - lo || 1)) * (H - pt - pb)
  const color = (key: string) => PALETTE[series.findIndex(s => s.key === key) % PALETTE.length] as string
  // the switchover: after the last closed period, or partway through a period that spans the cut-off
  const closedShare = (i: number) => {
    const p = x[i]
    if (p?.period?.months) return Math.min(1, p.period.closedMonths / p.period.months)
    return p?.closed ? 1 : 0
  }
  let cut = 0
  for (let i = 0; i < n; i++) {
    const f = closedShare(i)
    if (f <= 0) break
    cut = i + f
    if (f < 1) break
  }
  const step = n > 1 ? cx(1) - cx(0) : band
  // position of "cut" periods along the axis: period i spans [cx(i) - step/2, cx(i) + step/2]
  const cutX = cut > 0 ? Math.min(W - pr, Math.max(pl, cx(0) - step / 2 + cut * step)) : null
  const hasForecast = cut < n
  const labelEvery = Math.max(1, Math.ceil(n / Math.floor((W - pl - pr) / (compact ? 58 : 64))))
  const short = (v: number) => fmt(v, format)

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - r.left) / r.width) * W
    let best = 0
    let d = Number.POSITIVE_INFINITY
    for (let i = 0; i < n; i++) {
      const dd = Math.abs(cx(i) - px)
      if (dd < d) {
        d = dd
        best = i
      }
    }
    setHover(px < pl - 8 ? null : best)
  }

  return (
    <div className="chart" ref={box}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={series.map(s => s.label).join(', ')}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {cutX !== null && (
          <g>
            <rect x={pl} y={pt} width={Math.max(0, cutX - pl)} height={H - pt - pb} fill="var(--actual-bg)" />
            {cutX - pl > 44 && (
              <text x={cutX - 5} y={pt + 11} textAnchor="end" className="chart-note">
                Actuals
              </text>
            )}
            {hasForecast && (
              <>
                <line x1={cutX} x2={cutX} y1={pt} y2={H - pb} stroke="var(--text)" strokeWidth={1} />
                {W - pr - cutX > 50 && (
                  <text x={cutX + 5} y={pt + 11} className="chart-note forecast">
                    Forecast
                  </text>
                )}
              </>
            )}
          </g>
        )}
        {ticks.map(t => (
          <g key={t}>
            <line x1={pl} x2={W - pr} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--border-strong)' : 'var(--border)'} />
            <text x={pl - 6} y={y(t) + 3.5} textAnchor="end" className="chart-axis">
              {short(t)}
            </text>
          </g>
        ))}
        {x.map((p, i) =>
          i % labelEvery === 0 ? (
            <text key={p.id} x={cx(i)} y={H - 7} textAnchor="middle" className="chart-axis">
              {p.label}
            </text>
          ) : null,
        )}
        {mode === 'bar'
          ? x.map((_, i) => {
              let acc = 0
              const w = Math.max(2, Math.min(38, (band * 0.72) / (stacked ? 1 : shown.length)))
              return shown.map((s, k) => {
                const v = s.values[i]
                if (v === null || v === undefined) return null
                const x0 = stacked ? cx(i) - w / 2 : cx(i) - (w * shown.length) / 2 + k * w
                const top = stacked ? y(acc + v) : y(Math.max(0, v))
                const bottom = stacked ? y(acc) : y(Math.min(0, v))
                if (stacked) acc += v
                return (
                  <rect
                    key={`${s.key}-${i}`}
                    x={x0}
                    y={top}
                    width={w - (stacked ? 0 : 1)}
                    height={Math.max(0.5, bottom - top)}
                    fill={color(s.key)}
                    opacity={hover === null || hover === i ? 0.92 : 0.45}
                  />
                )
              })
            })
          : shown.map(s => {
              let d = ''
              let pen = false
              s.values.forEach((v, i) => {
                if (v === null) {
                  pen = false
                  return
                }
                d += `${pen ? 'L' : 'M'}${cx(i).toFixed(1)},${y(v).toFixed(1)}`
                pen = true
              })
              return (
                <g key={s.key}>
                  <path d={d} fill="none" stroke={color(s.key)} strokeWidth={1.75} strokeDasharray={s.dashed ? '5 4' : undefined} />
                  {n <= 16 &&
                    s.values.map((v, i) =>
                      v === null ? null : (
                        // actual points filled, forecast points hollow
                        <circle
                          key={i}
                          cx={cx(i)}
                          cy={y(v)}
                          r={2.25}
                          fill={closedShare(i) >= 1 ? color(s.key) : 'var(--surface)'}
                          stroke={color(s.key)}
                          strokeWidth={1.5}
                        />
                      ),
                    )}
                </g>
              )
            })}
        {hover !== null && (
          <g pointerEvents="none">
            <line x1={cx(hover)} x2={cx(hover)} y1={pt} y2={H - pb} stroke="var(--text)" strokeWidth={0.75} />
            {mode === 'line' &&
              shown.map(s => {
                const v = s.values[hover]
                return v === null || v === undefined ? null : <circle key={s.key} cx={cx(hover)} cy={y(v)} r={3.5} fill={color(s.key)} />
              })}
          </g>
        )}
      </svg>
      {hover !== null && x[hover] && (
        <div className="chart-tip" style={{ left: Math.min(Math.max(cx(hover) + 12, 0), W - 190) }}>
          <div className="chart-tip-head">
            {x[hover]?.label}
            {x[hover]?.closed ? <span className="faint"> · actual</span> : null}
          </div>
          {shown.map(s => (
            <div key={s.key} className="chart-tip-row">
              <span className="swatch" style={{ background: color(s.key) }} />
              <span className="grow">{s.label}</span>
              <span className="num">{s.values[hover] === null ? '—' : fmt(s.values[hover], format)}</span>
            </div>
          ))}
          {stacked && (
            <div className="chart-tip-row strong">
              <span className="swatch" />
              <span className="grow">Total</span>
              <span className="num">
                {fmt(
                  shown.reduce((a, s) => a + (s.values[hover] ?? 0), 0),
                  format,
                )}
              </span>
            </div>
          )}
        </div>
      )}
      {series.length > 1 && (
        <div className="chart-legend">
          {series.map(s => (
            <button
              key={s.key}
              type="button"
              className={hidden.has(s.key) ? 'off' : ''}
              onClick={() =>
                setHidden(h => {
                  const next = new Set(h)
                  if (next.has(s.key)) next.delete(s.key)
                  else if (next.size < series.length - 1) next.add(s.key)
                  return next
                })
              }
              title={hidden.has(s.key) ? 'Show' : 'Hide'}
            >
              <span
                className={`swatch ${s.dashed ? 'dashed' : ''}`}
                style={{ background: s.dashed ? undefined : color(s.key), borderColor: color(s.key) }}
              />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
